import { useState, useEffect } from 'react';
import { UserPlus, Loader2, Trash2, Crown, Eye, Edit3, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useFamily } from '@/context/FamilyContext';
import { TopBar } from '@/components/TopBar';
import { EmptyState } from '@/components/EmptyState';
import type { Member, Profile } from '@/types';

export function Family() {
  const { user } = useAuth();
  const { family, members, isOwner, reload } = useFamily();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'view' | 'edit'>('edit');
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [ownerProfile, setOwnerProfile] = useState<Profile | null>(null);

  // When viewing as a non-owner member, fetch the actual owner's profile
  useEffect(() => {
    if (!isOwner && family) {
      supabase
        .from('profiles')
        .select('*')
        .eq('id', family.owner_id)
        .maybeSingle()
        .then(({ data }) => setOwnerProfile(data as Profile | null));
    }
  }, [isOwner, family]);

  if (!family) return null;

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);

    const { error } = await supabase.from('members').insert({
      family_id: family.id,
      invited_email: inviteEmail.trim().toLowerCase(),
      role: inviteRole,
      status: 'pending'
    });

    setInviting(false);
    if (error) {
      toast.error(error.code === '23505' ? 'This email is already invited.' : error.message);
      return;
    }
    toast.success(`Invite saved for ${inviteEmail} — ask them to open the app and sign in.`);
    setInviteEmail('');
    reload();
  };

  const changeRole = async (member: Member, role: 'view' | 'edit') => {
    const { error } = await supabase.from('members').update({ role }).eq('id', member.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Role updated');
    reload();
  };

  const removeMember = async (member: Member) => {
    if (!confirm(`Remove ${member.invited_email}?`)) return;
    setRemovingId(member.id);
    const { error } = await supabase.from('members').delete().eq('id', member.id);
    setRemovingId(null);
    if (error) { toast.error(error.message); return; }
    toast.success('Member removed');
    reload();
  };

  const active = members.filter((m) => m.status === 'active');
  const pending = members.filter((m) => m.status === 'pending');

  // Total visible members count: owner + active members
  const totalCount = 1 + active.length;

  return (
    <div>
      <TopBar title="Family" />
      <div className="px-4 py-4 space-y-5">

        {/* Family name */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-400 mb-1">Family</p>
          <p className="font-semibold text-gray-900 text-lg">{family.name}</p>
          {isOwner && (
            <div className="flex items-center gap-1.5 mt-2 text-amber-600">
              <Crown className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">You are the owner</span>
            </div>
          )}
        </div>

        {/* Invite (owner only) */}
        {isOwner && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
            <h2 className="font-semibold text-sm text-gray-800">Invite a member</h2>
            <p className="text-xs text-gray-400">After inviting, ask them to open the app and sign in with their Google account.</p>
            <form onSubmit={handleInvite} className="space-y-3">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="family@gmail.com"
                required
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="flex gap-3">
                {(['view', 'edit'] as const).map((r) => (
                  <label
                    key={r}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border-2 rounded-xl cursor-pointer transition-colors text-sm font-medium"
                    style={{
                      borderColor: inviteRole === r ? '#4f46e5' : '#e5e7eb',
                      background: inviteRole === r ? '#eef2ff' : 'white',
                      color: inviteRole === r ? '#4338ca' : '#6b7280'
                    }}
                  >
                    <input type="radio" name="role" value={r} checked={inviteRole === r} onChange={() => setInviteRole(r)} className="sr-only" />
                    {r === 'view' ? <><Eye className="w-4 h-4" /> View only</> : <><Edit3 className="w-4 h-4" /> Can edit</>}
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={inviting}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {inviting ? 'Saving…' : 'Add invite'}
              </button>
            </form>
          </div>
        )}

        {/* Members list */}
        <div>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Members ({totalCount})
          </h2>
          <div className="space-y-2">
            {/* Owner row — always shown at top, correctly identified */}
            {isOwner ? (
              <MemberRow
                email={user?.email ?? ''}
                displayName={user?.user_metadata?.full_name ?? user?.email ?? ''}
                role="owner"
                status="active"
                showOwnerControls={false}
                isSelf
              />
            ) : ownerProfile ? (
              <MemberRow
                email={ownerProfile.email}
                displayName={ownerProfile.display_name ?? ownerProfile.email}
                role="owner"
                status="active"
                showOwnerControls={false}
                isSelf={ownerProfile.id === user?.id}
              />
            ) : null}

            {/* Active members (excluding the current owner if they somehow appear) */}
            {active
              .filter((m) => m.user_id !== family.owner_id)
              .map((m) => (
                <MemberRow
                  key={m.id}
                  email={m.invited_email}
                  displayName={m.profiles?.display_name ?? m.invited_email}
                  role={m.role}
                  status="active"
                  showOwnerControls={isOwner}
                  isSelf={m.user_id === user?.id}
                  onChangeRole={(role) => changeRole(m, role)}
                  onRemove={() => removeMember(m)}
                  removing={removingId === m.id}
                />
              ))}
          </div>
        </div>

        {/* Pending invites */}
        {pending.length > 0 && (
          <div>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Pending invites ({pending.length})
            </h2>
            <div className="space-y-2">
              {pending.map((m) => (
                <MemberRow
                  key={m.id}
                  email={m.invited_email}
                  displayName={m.invited_email}
                  role={m.role}
                  status="pending"
                  showOwnerControls={isOwner}
                  isSelf={false}
                  onChangeRole={(role) => changeRole(m, role)}
                  onRemove={() => removeMember(m)}
                  removing={removingId === m.id}
                />
              ))}
            </div>
          </div>
        )}

        {active.length === 0 && pending.length === 0 && isOwner && (
          <EmptyState
            icon={<Users className="w-7 h-7" />}
            title="No members yet"
            description="Invite family members above. Ask them to open the app and sign in with Google."
          />
        )}
      </div>
    </div>
  );
}

function MemberRow({
  email, displayName, role, status, showOwnerControls, isSelf,
  onChangeRole, onRemove, removing
}: {
  email: string;
  displayName: string;
  role: 'owner' | 'view' | 'edit';
  status: 'active' | 'pending';
  showOwnerControls: boolean;
  isSelf?: boolean;
  onChangeRole?: (role: 'view' | 'edit') => void;
  onRemove?: () => void;
  removing?: boolean;
}) {
  const initial = (displayName?.[0] ?? email[0] ?? '?').toUpperCase();

  return (
    <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
      <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
        <span className="text-brand-700 text-sm font-semibold">{initial}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          {isSelf && <span className="text-xs text-gray-400">(you)</span>}
        </div>
        <p className="text-xs text-gray-400 truncate">{email}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {status === 'pending' && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending</span>
        )}
        {role === 'owner' && <Crown className="w-3.5 h-3.5 text-amber-500" />}
        {showOwnerControls && role !== 'owner' && (
          <select
            value={role}
            onChange={(e) => onChangeRole?.(e.target.value as 'view' | 'edit')}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="view">View</option>
            <option value="edit">Edit</option>
          </select>
        )}
        {showOwnerControls && role !== 'owner' && (
          <button
            onClick={onRemove}
            disabled={removing}
            className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors"
          >
            {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}
